import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link, useTheme } from '@material-ui/core';
import Typography, { fontStyle } from 'core/ui/Typography';
import Box from 'core/ui/Box';
import LoaderSm from 'core/ui/LoaderSm';
import Divider from 'core/ui/Divider';
import { date } from 'core/utils/formatters';
import SFDataTable from 'core/ui/DataTable';
import ImageMedia from 'components/Profile/ImageMedia';
import LabelPhone from 'core/ui/InputPhone/LabelPhone';
import { ButtonPrimarySm } from 'core/ui/Button';
import { useSelector } from 'react-redux';
import { sessionSelectors } from 'modules/session';

const TableMembers = ({
  setModalMemberInformation,
  members,
  pagination,
  loading,
  search,
  setPerPage,
  perPage,
  changeLead,
  inactiveMember,
  channel,
}) => {
  const { palette } = useTheme();
  const isAdmin = useSelector((state) => sessionSelectors.getIsAdmin(state));
  const { t } = useTranslation();

  const canOpenProfile = (member) => {
    return !!member.memberTypes.find(
      (y) =>
        !!['user', 'membership_inactive', 'membership_active', 'child', 'lead', 'customer'].find(
          (x) => x === y,
        ),
    );
  };

  const columns = [
    {
      name: '',
      selector: 'name',
      cell: (member) => (
        <Link
          className="hover"
          onClick={() =>
            canOpenProfile(member)
              ? setModalMemberInformation({
                  userId: member?.id,
                  open: true,
                })
              : null
          }
        >
          <Box my={1}>
            <ImageMedia
              user={member}
              image={member.image ? member.image : {}}
              size={50}
              heightCountry={15}
              sizeCountry="1em"
            />
          </Box>
        </Link>
      ),
      sortable: false,
      width: '60px',
    },
    {
      name: t('channel.memberships.column.user'),
      selector: 'name',
      cell: (member) => (
        <Link
          className="hover"
          onClick={() =>
            canOpenProfile(member)
              ? setModalMemberInformation({
                  userId: member?.id,
                  open: true,
                })
              : null
          }
        >
          <Box>
            <Box>
              <Typography mr={1} color={canOpenProfile(member) ? 'primary' : 'secondary'}>
                {member.name || member?.email}
              </Typography>
            </Box>
            {!!member.parentName && (
              <Typography color="secondary" mr={1} style={fontStyle.small}>
                Parent: {member.parentName}
              </Typography>
            )}
          </Box>
        </Link>
      ),
      sortable: false,
      width: '250px',
    },
    {
      name: t('channel.memberships.column.email'),
      selector: 'email',
      sortable: false,
      width: '230px',
      cell: (member) => (
        <Typography color="secondary" mr={1} style={fontStyle.label}>
          {member.email}
        </Typography>
      ),
    },
    {
      name: t('channel.memberships.column.phone'),
      selector: 'phone',
      sortable: false,
      width: '230px',
      cell: (member) => <>{member.phone && <LabelPhone value={member.phone} />}</>,
    },
    {
      name: t('channel.memberships.column.status'),
      selector: 'keys',
      sortable: false,
      cell: (member) => {
        const memberKeys = member.keys || [];
        const startPlan = memberKeys.find((x) => !!x.notStarted);
        const activeKeys = memberKeys.filter((x) => !x.expired).filter((x) => !x.notStarted);
        return (
          <>
            <Box>
              {startPlan && (
                <>
                  <Typography color="secondary" style={{ ...fontStyle.small }}>
                    {startPlan.membershipPlan.name}
                  </Typography>
                  <Typography color="primary" style={{ ...fontStyle.small }}>
                    {t('channel.label.plan.startDate')}&nbsp;
                    {date(startPlan.startAt, channel?.dateFormatReact)}
                  </Typography>
                  <Divider />
                </>
              )}
              {member.keys &&
                activeKeys.map((key, i) => (
                  <Box key={i.toString()}>
                    {!!key.membership && key.membershipPlan && (
                      <>
                        <Typography color="secondary" style={{ ...fontStyle.small }}>
                          {key.membershipPlan && key.membershipPlan.name}
                        </Typography>

                        {key.membershipPlan.online && key.membershipPlan.inPerson && (
                          <Typography color="primary" style={{ ...fontStyle.small }}>
                            {t('workout.setting.online')},&nbsp;
                            {t('workout.setting.inPerson')}
                          </Typography>
                        )}
                        {!key.membershipPlan.online && key.membershipPlan.inPerson && (
                          <Typography color="primary" style={{ ...fontStyle.small }}>
                            {t('workout.setting.inPerson')}
                          </Typography>
                        )}
                        {key.membershipPlan.online && !key.membershipPlan.inPerson && (
                          <Typography color="primary" style={{ ...fontStyle.small }}>
                            {t('workout.setting.online')}
                          </Typography>
                        )}
                        {!key.pauseAt && key.expiryAt && (
                          <Typography
                            color="secondary"
                            style={{ ...fontStyle.small, color: 'grey' }}
                          >
                            {t(key.renewable ? 'channel.memberships.renewIn' : 'expires')}&nbsp;
                            {date(key.expiryAt, channel?.dateFormatReact)}
                          </Typography>
                        )}
                        {!!key.pauseAt && key.expiryAt && (
                          <Typography
                            color="secondary"
                            style={{ ...fontStyle.small, color: 'grey' }}
                          >
                            {t('pausedAt')}&nbsp;
                            {date(key.pauseAt, channel?.dateFormatReact)}
                          </Typography>
                        )}
                        {key.planExpiryAt && (
                          <Typography color="primary" style={{ ...fontStyle.small }}>
                            {t('channel.memberships.planExpiresIn')}&nbsp;
                            {date(key.planExpiryAt, channel?.dateFormatReact)}
                          </Typography>
                        )}
                        {key?.limitUse && (
                          <Typography
                            color="secondary"
                            style={{ ...fontStyle.small, color: 'grey' }}
                          >
                            {key?.limitUse}
                          </Typography>
                        )}
                      </>
                    )}
                    {!key.membership && (
                      <>
                        <Box>
                          <Typography color="secondary" style={{ ...fontStyle.small }}>
                            Channel Key
                          </Typography>
                        </Box>
                        <Typography color="primary" style={{ ...fontStyle.small }}>
                          {key.kind === 'online'
                            ? t('workout.setting.online')
                            : t('workout.setting.inPerson')}
                        </Typography>
                      </>
                    )}

                    {i < member.keys.length - 1 && <Divider />}
                  </Box>
                ))}
            </Box>
          </>
        );
      },
    },
    {
      name: t('channel.memberships.column.joinedDate'),
      selector: 'joinedDate',
      sortable: false,
      cell: (member) => <Typography>{member.memberAnniversaryDate}</Typography>,
    },
    {
      name: t('channel.memberships.column.card'),
      selector: 'defaultPaymentInfo',
      sortable: false,
      cell: (member) => <Typography>{member.defaultPaymentInfo}</Typography>,
    },
    {
      name: '',
      selector: '',
      sortable: false,
      cell: (member) => (
        <Box display="flex" alignItems="center" mb={1}>
          {member.memberTypes.map((tp, idx) => (
            <Box
              key={idx.toString()}
              mr={1}
              style={{
                backgroundColor: palette.primary.main,
                padding: 3,
                borderRadius: 10,
                color: 'white',
              }}
              className="hover"
            >
              <Typography style={fontStyle.small} noWrap>
                {t(`channel.memberships.column.type.${tp}`)}
              </Typography>
            </Box>
          ))}
          {member?.memberTypes?.find((x) => x === 'lead') && (
            <ButtonPrimarySm onClick={() => changeLead(member)}>Uncked Lead</ButtonPrimarySm>
          )}
          &nbsp;
          {isAdmin && (
            <ButtonPrimarySm onClick={() => inactiveMember(member)}>Make Inactive</ButtonPrimarySm>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <SFDataTable
        title=""
        noHeader
        paginationRowsPerPageOptions={[10, 25, 50]}
        showPaper={false}
        columns={columns}
        data={members}
        exportData={members.map((member) => ({
          userName: member.name,
          userPhone: member.phone,
          userEmail: member.email,
          DBO: member.birthday || '',
          joinedDate: member.memberAnniversaryDate || '',
        }))}
        showExport={false}
        pagination
        noDataComponent=""
        paginationPerPage={perPage}
        onChangePage={(p) => search(p)}
        paginationServer
        paginationDefaultPage={pagination?.currentPage}
        paginationTotalRows={pagination?.totalCount}
        onChangeRowsPerPage={(newValue) => {
          setPerPage(newValue);
        }}
      />

      <Box display="flex" justifyContent="center">
        <LoaderSm loading={loading} />
      </Box>
    </>
  );
};

TableMembers.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array,
  setModalMemberInformation: PropTypes.func,
  loading: PropTypes.bool,
};

TableMembers.defaultProps = {
  members: [],
  setModalMemberInformation: () => {},
  loading: false,
};

export default TableMembers;
